// tenant.ts — subdomain-per-tenant resolution (W5).
//
// The host's leftmost label identifies the tenant for BRANDING + discovery only (e.g.
// healthplus.mypharmacyiq.com → "healthplus"). It is NEVER the authorization boundary — that is the
// JWT claim + database RLS. The backend additionally enforces a strict login guard (a user may only
// sign in on their own tenant's subdomain), so this layer is purely cosmetic/UX.

import { useQuery } from '@tanstack/react-query';
import { ApiError, apiFetch } from '@/api/client';

// Apex the tenant subdomains live under. Mirrors the backend's TENANT_BASE_DOMAIN; override per
// environment with VITE_TENANT_BASE_DOMAIN (e.g. a staging apex).
const APEX = (import.meta.env.VITE_TENANT_BASE_DOMAIN ?? 'mypharmacyiq.com').toLowerCase();
// Only `www` (onboarding/marketing) is exempt from tenant resolution on the apex — every other
// `*.mypharmacyiq.com` host MUST resolve to a real tenant or the gate redirects it to onboarding.
// (`api`/`mapi` are backend hosts the SPA never loads on, so they don't need listing here.)
const RESERVED = new Set(['www']);

/** The tenant subdomain label for a host, or null when it isn't a single-label subdomain of the
 *  apex (localhost, *.azurestaticapps.net, the raw App Service host, the apex itself, or `www`). */
export function tenantSubdomainFromHost(host: string = window.location.hostname): string | null {
  const h = host.toLowerCase();
  const suffix = `.${APEX}`;
  if (!h.endsWith(suffix)) return null;
  const label = h.slice(0, -suffix.length);
  if (!label || label.includes('.') || RESERVED.has(label)) return null;
  return label;
}

export interface TenantBranding {
  id: string;
  name: string;
  subdomain: string;
}

/** Public, pre-login lookup of the current subdomain's tenant — themes the login page. Resolves to
 *  null for an unknown workspace (a 404), which the login page turns into the redirect splash.
 *  Disabled (no data) when not on a tenant subdomain. */
export function useTenantBranding() {
  const subdomain = tenantSubdomainFromHost();
  return useQuery<TenantBranding | null>({
    queryKey: ['tenant-branding', subdomain],
    enabled: !!subdomain,
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      try {
        return await apiFetch<TenantBranding>(
          `/api/identity/tenants/resolve/?subdomain=${subdomain}`,
        );
      } catch (err) {
        // A real 404 means "no such workspace" → resolve to null (the login page shows the redirect
        // splash). Anything else is transient (network / 5xx) → rethrow so a blip doesn't bounce us.
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
  });
}
